import * as XLSX from 'xlsx-js-style';
import { supabase } from './supabase';
import { embedToArray } from './utils';
import { getStatusRateioUc, STATUS_RATEIO_UC_META } from './statusRateioUc';
import { mesParaIndice } from './analiseTecnicaConsumo';
import { carregarDatasFaturas } from '../services/datasFaturasApi';
import {
    chavesDaUc, dataDaDecisao, dataIsoParaBr, dataMaisRecente, extracaoMaisRecentePorUc, inclusaoNoRateio,
    indexarFaturas, indexarListasRateio, lerNumeroBr, mesesDoHistoricoDaFatura, numerosUcDaNegociacao,
    percentualDeFracao, primeiraFatura, primeiroDia, rotuloDecisaoAnalise, situacaoNoCrm, ucsPeloNumeroDaNegociacao,
    type ExtracaoResumo, type FaturaComDatas, type ItemListaRateio, type LinhaUcDaNegociacao, type MesConsumo,
} from './exportAnaliseCadastralDados';

// ─── Branding Colors ───────────────────────────────────────────────────
const CLR = {
    headerBg: '212913',      // primary-dark
    headerFg: 'FFFFFF',
    subHeaderBg: '5F6C37',   // primary
    subHeaderFg: 'FFFFFF',
    sectionBg: 'EFF2E6',     // light green tint
    sectionFg: '2D3317',
    zebraDark: 'F5F7F0',     // very light green zebra
    zebraLight: 'FFFFFF',
    borderColor: 'D6DCC4',
    redBg: 'FEF2F2',
    redFg: 'B91C1C',
    greenBg: 'F0FDF4',
    greenFg: '15803D',
    orangeBg: 'FFFBEB',
    orangeFg: 'B45309',
    blueBg: 'EFF6FF',
    blueFg: '1D4ED8',
};

// ─── Helpers ───────────────────────────────────────────────────────────
const parseBrNumber = lerNumeroBr;

const fmtDate = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// O rótulo → índice absoluto vem de `analiseTecnicaConsumo` (`mesParaIndice`),
// que é quem também ordena o histórico para decidir os meses mais recentes da
// média. Duas cópias divergiriam no primeiro formato novo de rótulo.

const indiceParaLabel = (indice: number): string =>
    `${MONTH_NAMES[indice % 12]}/${String(Math.floor(indice / 12)).slice(-2)}`;

const indiceDoMesAtual = (): number => {
    const hoje = new Date();
    return hoje.getFullYear() * 12 + hoje.getMonth();
};

const NUM_HISTORY_SLOTS = 12;

/**
 * Colunas de consumo mensal. Cada UC guarda 12 meses, mas o período varia de
 * UC pra UC (análise de março tem histórico que termina em março). Antes o
 * cabeçalho vinha da primeira UC com histórico e valia pra planilha inteira,
 * então 279 de 309 linhas mostravam o consumo debaixo do mês errado. Agora as
 * colunas são calendário de verdade e cada consumo cai no seu mês — a janela
 * é maior que 12 justamente pra caber quem foi analisado há mais tempo.
 */
const JANELA_MESES = 18;

/**
 * Checks técnicos da UC são tri-estado: true, false e "nunca respondido".
 * Um `false` e um `undefined` significam coisas diferentes na planilha — o
 * primeiro é uma resposta da analista, o segundo é ausência de análise — então
 * célula vazia para undefined/null em vez de "Não".
 */
const simNao = (v: unknown): string => (v === true ? 'Sim' : v === false ? 'Não' : '');

/**
 * UC sem máscara. A base grava o número ora limpo, ora formatado
 * ("3.596.344.012-44") — 154 das 275 UCs das negociações aprovadas em
 * 13/08/2026. Como a planilha é cruzada por PROCV com a lista do rateio, que
 * vem só com dígitos, a UC formatada não bate e aparece como se tivesse ficado
 * de fora. Sai texto (não número) de propósito: tem UC com zero à esquerda.
 */
const soDigitos = (v?: string | null): string => (v ? String(v).replace(/\D/g, '') : '');

// ─── Buscas fora da negociação ─────────────────────────────────────────
const LOTE_NUMEROS_UC = 150;   // cabe na URL do filtro `in.(...)` com folga
const PAGINA = 1000;           // limite de linhas por resposta do PostgREST

/**
 * UCs sem `negociacao_id` cujo número atual ou antigo está na lista — o cadastro
 * antigo que a negociação só referencia pelo número (ver `ucsPeloNumeroDaNegociacao`).
 */
async function buscarUcsSemVinculo(numeros: string[]): Promise<any[]> {
    const porId = new Map<number, any>();
    for (let i = 0; i < numeros.length; i += LOTE_NUMEROS_UC) {
        const lote = numeros.slice(i, i + LOTE_NUMEROS_UC).join(',');
        const { data, error } = await supabase
            .from('unidades_consumidoras')
            .select('*')
            .is('negociacao_id', null)
            .or(`numero_uc_norm.in.(${lote}),outro_numero_uc_norm.in.(${lote})`);
        if (error) throw new Error(error.message);
        // A mesma UC pode vir em dois lotes (número atual num, antigo no outro).
        for (const uc of data || []) porId.set(uc.id, uc);
    }
    return [...porId.values()];
}

/**
 * Quadro de consumo da fatura mais recente de cada UC (normalizada). Em duas
 * etapas porque o histórico é JSON pesado: primeiro só mês e status de todas as
 * extrações, depois o histórico apenas da escolhida.
 */
async function buscarHistoricoDasFaturas(ucs: string[], ultimoMesValido: number): Promise<Map<string, MesConsumo[]>> {
    const resumos: ExtracaoResumo[] = [];
    for (let i = 0; i < ucs.length; i += LOTE_NUMEROS_UC) {
        const lote = ucs.slice(i, i + LOTE_NUMEROS_UC);
        for (let de = 0; ; de += PAGINA) {
            const { data, error } = await supabase
                .from('extracoes_faturas')
                .select('id, uc_codigo_norm, mes_referencia, status')
                .in('uc_codigo_norm', lote)
                .order('id')
                .range(de, de + PAGINA - 1);
            if (error) throw new Error(error.message);
            if (!data || data.length === 0) break;
            resumos.push(...(data as ExtracaoResumo[]));
        }
    }

    const escolhida = extracaoMaisRecentePorUc(resumos);
    const ucDaExtracao = new Map([...escolhida].map(([uc, id]) => [id, uc]));
    const ids = [...ucDaExtracao.keys()];
    const historicos = new Map<string, MesConsumo[]>();
    for (let i = 0; i < ids.length; i += LOTE_NUMEROS_UC) {
        const { data, error } = await supabase
            .from('extracoes_faturas')
            .select('id, historico:dados_estruturados->historico')
            .in('id', ids.slice(i, i + LOTE_NUMEROS_UC));
        if (error) throw new Error(error.message);
        for (const e of (data || []) as { id: number; historico: any }[]) {
            const meses = mesesDoHistoricoDaFatura(Array.isArray(e.historico) ? e.historico : null, ultimoMesValido);
            const uc = ucDaExtracao.get(e.id);
            if (uc && meses.length > 0) historicos.set(uc, meses);
        }
    }
    return historicos;
}

/** Todos os itens das listas mensais de rateio (Gestão › Rateio Antigo), com o mês da lista. */
async function buscarListasRateio(): Promise<ItemListaRateio[]> {
    const { data: listas, error } = await supabase.from('rateios_credito').select('id, mes_referencia');
    if (error) throw new Error(error.message);
    const mesDaLista = new Map((listas || []).map(l => [l.id, l.mes_referencia as string | null]));

    const itens: ItemListaRateio[] = [];
    for (let de = 0; ; de += PAGINA) {
        const { data, error: erroItens } = await supabase
            .from('rateios_credito_itens')
            .select('uc, outro_numero_uc, rateio_id')
            .order('id')
            .range(de, de + PAGINA - 1);
        if (erroItens) throw new Error(erroItens.message);
        if (!data || data.length === 0) break;
        for (const item of data) {
            itens.push({ uc: item.uc, outro_numero_uc: item.outro_numero_uc, mes_referencia: mesDaLista.get(item.rateio_id) ?? null });
        }
    }
    return itens;
}

// ─── Cell Style Builders ───────────────────────────────────────────────
const baseFont = { name: 'Aptos', sz: 10 };

const titleStyle: XLSX.CellStyle = {
    font: { name: 'Aptos', sz: 16, bold: true, color: { rgb: CLR.headerFg } },
    fill: { fgColor: { rgb: CLR.headerBg } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: { bottom: { style: 'medium', color: { rgb: CLR.subHeaderBg } } },
};

const headerStyle: XLSX.CellStyle = {
    font: { ...baseFont, sz: 10, bold: true, color: { rgb: CLR.headerFg } },
    fill: { fgColor: { rgb: CLR.subHeaderBg } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
        bottom: { style: 'thin', color: { rgb: CLR.borderColor } },
        right: { style: 'hair', color: { rgb: '88956A' } },
    },
};

const dataStyle = (isEven: boolean): XLSX.CellStyle => ({
    font: { ...baseFont, color: { rgb: '1F2937' } },
    fill: { fgColor: { rgb: isEven ? CLR.zebraDark : CLR.zebraLight } },
    alignment: { vertical: 'center', horizontal: 'left' },
    border: {
        bottom: { style: 'hair', color: { rgb: CLR.borderColor } },
        right: { style: 'hair', color: { rgb: CLR.borderColor } },
    },
});

const dataCenterStyle = (isEven: boolean): XLSX.CellStyle => ({
    ...dataStyle(isEven),
    alignment: { ...dataStyle(isEven).alignment, horizontal: 'center' },
});

const dataNumStyle = (isEven: boolean): XLSX.CellStyle => ({
    ...dataStyle(isEven),
    alignment: { ...dataStyle(isEven).alignment, horizontal: 'right' },
    numFmt: '#,##0',
});

const getStatusStyle = (status: string, isEven: boolean): XLSX.CellStyle => {
    let bg = isEven ? CLR.zebraDark : CLR.zebraLight;
    let fg = '1F2937';
    if (status.includes('Aprovado') && !status.includes('Ressalva')) { bg = CLR.greenBg.replace('#', ''); fg = CLR.greenFg; }
    else if (status.includes('Ressalva')) { bg = CLR.blueBg.replace('#', ''); fg = CLR.blueFg; }
    else if (status.includes('Reprovado')) { bg = CLR.redBg.replace('#', ''); fg = CLR.redFg; }
    // Pendência/garantia ainda em análise: laranja, como o "Termo em análise" do Kanban.
    else if (status.includes('Pendência') || status.includes('Garantia') || status === 'Em Análise') { bg = CLR.orangeBg.replace('#', ''); fg = CLR.orangeFg; }

    return {
        font: { ...baseFont, bold: true, color: { rgb: fg } },
        fill: { fgColor: { rgb: bg } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
            bottom: { style: 'hair', color: { rgb: CLR.borderColor } },
            right: { style: 'hair', color: { rgb: CLR.borderColor } },
        },
    };
};

const getDelayStyle = (avg: number, isEven: boolean): XLSX.CellStyle => {
    let bg = isEven ? CLR.zebraDark : CLR.zebraLight;
    let fg = '1F2937';
    if (avg > 15) { bg = CLR.redBg.replace('#', ''); fg = CLR.redFg; }
    else if (avg > 5) { bg = CLR.orangeBg.replace('#', ''); fg = CLR.orangeFg; }
    else if (avg <= 5) { bg = CLR.greenBg.replace('#', ''); fg = CLR.greenFg; }

    return {
        font: { ...baseFont, bold: true, color: { rgb: fg } },
        fill: { fgColor: { rgb: bg } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { bottom: { style: 'hair', color: { rgb: CLR.borderColor } }, right: { style: 'hair', color: { rgb: CLR.borderColor } } },
    };
};

const getRiscoStyle = (risco: string, isEven: boolean): XLSX.CellStyle => {
    let bg = isEven ? CLR.zebraDark : CLR.zebraLight;
    let fg = '1F2937';
    if (risco === 'A' || risco === 'B') { bg = CLR.greenBg.replace('#', ''); fg = CLR.greenFg; }
    else if (risco === 'C') { bg = CLR.orangeBg.replace('#', ''); fg = CLR.orangeFg; }
    else if (risco === 'D' || risco === 'E') { bg = CLR.redBg.replace('#', ''); fg = CLR.redFg; }

    return {
        font: { ...baseFont, bold: true, sz: 11, color: { rgb: fg } },
        fill: { fgColor: { rgb: bg } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { bottom: { style: 'hair', color: { rgb: CLR.borderColor } }, right: { style: 'hair', color: { rgb: CLR.borderColor } } },
    };
};

// ─── Main Export Function ─────────────────────────────────────────────
export interface ExportAnaliseCadastralOptions {
    negociacaoIds: number[];
    profiles: { id: string; full_name: string }[];
}

export async function exportAnaliseCadastralToExcel(opts: ExportAnaliseCadastralOptions): Promise<void> {
    const { negociacaoIds, profiles } = opts;

    if (negociacaoIds.length === 0) return;

    // Fetch full data
    const { data: negs, error } = await supabase
        .from('negociacoes')
        .select(`
            id, cliente_nome, cpf_cnpj, tipo_cliente, whatsapp, email, numero_uc,
            consumo_medio_kwh, data_assinatura_termo, responsavel_id, status, stage, pipeline_id,
            unidades_consumidoras(*),
            analise_cadastral(*)
        `)
        // Sem order a ordem é a que o Postgres devolver, e muda de uma exportação
        // pra outra — duas planilhas do mesmo dia não davam pra comparar linha a
        // linha. Mesma ordenação da tela: assinatura mais antiga primeiro.
        .in('id', negociacaoIds)
        .order('data_assinatura_termo', { ascending: true, nullsFirst: false })
        .order('id', { ascending: true });

    if (error || !negs) {
        console.error('Export fetch error:', error);
        throw new Error('Erro ao buscar dados para exportação');
    }

    // ── Dados de fora da negociação ─────────────────────────────────
    // Negociação aprovada antes do cadastro de UC entrar no fluxo não tem UC com
    // negociacao_id: a UC existe, mas só é achada pelo número. Sem isso, ~360
    // linhas saíam só com nome e CPF (o CPF ainda ia parar na coluna UC).
    // Datas do ciclo e 1ª fatura vêm das faturas; entrada no rateio, das listas
    // mensais. Se uma busca dessas falhar a planilha sai assim mesmo, com aviso
    // no subtítulo — melhor que não exportar nada.
    const numerosSemVinculo = [...new Set(negs
        .filter(n => ((n.unidades_consumidoras || []) as any[]).length === 0)
        .flatMap(n => numerosUcDaNegociacao(n.numero_uc)))];
    const avisos: string[] = [];
    const tentar = async <T,>(buscar: () => Promise<T>, vazio: T, aviso: string): Promise<T> => {
        try {
            return await buscar();
        } catch (err) {
            console.error(`Export: ${aviso}`, err);
            avisos.push(aviso);
            return vazio;
        }
    };
    const [ucsSemVinculo, faturas, itensRateio, funis] = await Promise.all([
        tentar(() => buscarUcsSemVinculo(numerosSemVinculo), [] as any[], 'UCs do cadastro antigo'),
        tentar<FaturaComDatas[]>(() => carregarDatasFaturas(), [], 'datas das faturas'),
        tentar(buscarListasRateio, [] as ItemListaRateio[], 'listas de rateio'),
        tentar(async () => {
            const { data, error: erroFunis } = await supabase.from('pipelines').select('id, name');
            if (erroFunis) throw new Error(erroFunis.message);
            return (data || []) as { id: string; name: string }[];
        }, [] as { id: string; name: string }[], 'nomes dos funis'),
    ]);
    const nomeDoFunil = new Map(funis.map(f => [f.id, f.name]));
    const indiceFaturas = indexarFaturas(faturas, indiceDoMesAtual());
    const indiceRateio = indexarListasRateio(itensRateio);

    // Uma linha por UC. Sem UC vinculada, vale a UC achada pelo número que a
    // negociação traz — e o número sozinho, se nem cadastro tiver.
    const linhasPorNegociacao = new Map<number, LinhaUcDaNegociacao<any>[]>();
    for (const neg of negs) {
        const ucs = (neg.unidades_consumidoras || []) as any[];
        const linhas: LinhaUcDaNegociacao<any>[] = ucs.length > 0
            ? ucs.map(uc => ({ uc, numero: '' }))
            : ucsPeloNumeroDaNegociacao(neg.numero_uc, ucsSemVinculo);
        if (linhas.length === 0) linhas.push({ uc: null, numero: '' });
        linhasPorNegociacao.set(neg.id, linhas);
    }
    const chavesDaLinha = ({ uc, numero }: LinhaUcDaNegociacao<any>): string[] =>
        uc ? chavesDaUc(uc) : numerosUcDaNegociacao(numero);

    // UC que nunca passou pela análise técnica (cadastro antigo) não tem o quadro
    // de consumo — mas a fatura tem o mesmo quadro impresso, e ele está na extração.
    const temConsumoNaAnalise = (uc: any): boolean =>
        ((uc?.dados_tecnicos_equatorial?.historyTable || []) as any[])
            .slice(0, NUM_HISTORY_SLOTS)
            .some(e => e && !e.semRegistro && parseBrNumber(e.consumo) > 0);
    const ucsSemConsumo = [...new Set([...linhasPorNegociacao.values()].flat()
        .filter(linha => !temConsumoNaAnalise(linha.uc))
        .flatMap(chavesDaLinha))];
    const historicoFaturas = await tentar(
        () => buscarHistoricoDasFaturas(ucsSemConsumo, indiceDoMesAtual()),
        new Map<string, MesConsumo[]>(),
        'histórico de consumo das faturas',
    );
    /** Histórico da fatura mais recente entre os números da UC. */
    const historicoDaFatura = (chaves: string[]): MesConsumo[] => {
        let melhor: MesConsumo[] = [];
        for (const chave of chaves) {
            const meses = historicoFaturas.get(chave) || [];
            if (meses.length > 0 && (melhor.length === 0 || meses[0].indice > melhor[0].indice)) melhor = meses;
        }
        return melhor;
    };

    const profileName = (id?: string | null) => {
        if (!id) return '';
        return profiles.find(p => p.id === id)?.full_name || '';
    };

    // ── Janela de meses da planilha ─────────────────────────────────
    // Passada prévia por todas as UCs: a grade tem que existir antes das linhas,
    // porque é ela que define o cabeçalho. Ancora no mês mais recente que
    // aparece nos dados, ignorando mês futuro — há histórico com erro de virada
    // de ano na extração (…01/26, 12/26, 11/26… em vez de 12/25, 11/25), e
    // futuro nenhum é leitura real.
    const limiteMesAtual = indiceDoMesAtual();
    let mesMaisRecente: number | null = null;
    const considerarMes = (indice: number | null) => {
        if (indice === null || indice > limiteMesAtual) return;
        if (mesMaisRecente === null || indice > mesMaisRecente) mesMaisRecente = indice;
    };
    for (const linha of [...linhasPorNegociacao.values()].flat()) {
        for (const entry of (linha.uc?.dados_tecnicos_equatorial?.historyTable || []) as any[]) {
            considerarMes(mesParaIndice(entry?.month));
        }
        if (!temConsumoNaAnalise(linha.uc)) historicoDaFatura(chavesDaLinha(linha)).forEach(m => considerarMes(m.indice));
    }
    const fimJanela = mesMaisRecente ?? limiteMesAtual;
    // Mais novo → mais antigo, mesma leitura da planilha antiga.
    const mesesGrade: number[] = Array.from({ length: JANELA_MESES }, (_, i) => fimJanela - i);

    // ── Build rows — one row per UC ─────────────────────────────────
    const rows: any[][] = [];

    for (const neg of negs) {
        // embed vem como objeto (1→1) desde a UNIQUE em negociacao_id
        const analise = embedToArray(neg.analise_cadastral)[0] as any;
        const decisao = rotuloDecisaoAnalise(analise?.decisao_final, neg.status);
        const risco = analise?.motor_risco_classificacao || analise?.risco_final || '';
        // Mesmo texto da tela quando a negociação não tem dono (antes saía vazio).
        const vendedor = neg.responsavel_id ? profileName(neg.responsavel_id) : 'Sem responsável';
        const analista = profileName(analise?.analista_id);
        const dataAss = fmtDate(neg.data_assinatura_termo);
        const dataAnalise = fmtDate(dataDaDecisao(analise));
        const situacaoCrm = situacaoNoCrm(nomeDoFunil.get(neg.pipeline_id) ?? null, neg.stage, neg.status);
        const kwhNegociacao = Number(neg.consumo_medio_kwh) || 0;

        // ── Análise de Crédito (Serasa) — por negociação, repetida em cada linha de UC ──
        const serasa = (analise?.serasa_experian_data || {}) as any;
        const scoreSerasa = serasa.score ?? '';
        const chancePag = serasa.chance_pagamento != null && serasa.chance_pagamento !== ''
            ? `${String(serasa.chance_pagamento).replace('.', ',')}%`
            : '';
        const riscoCredito = serasa.risco_credito || '';
        const situacaoRF = (() => {
            let raw = serasa.situacao_receita_federal || '';
            const colonIdx = raw.lastIndexOf(':');
            if (colonIdx > -1) raw = raw.substring(colonIdx + 1).trim();
            const v = raw.toLowerCase();
            const map: Record<string, string> = { regular: 'Regular', pendente: 'Pendente', suspensa: 'Suspensa', cancelada: 'Cancelada', nula: 'Nula', inapta: 'Inapta', ativa: 'Ativa' };
            return map[v] || serasa.situacao_receita_federal || '';
        })();
        const scoreMotor = analise?.motor_risco_score ?? '';
        const riscoFinal = analise?.risco_final || '';
        const totalDividasQtd = (serasa.dividas_comerciais || 0) + (serasa.dividas_bancarias || 0) + (serasa.dividas_vencidas || 0) + (serasa.quantidade_protestos || 0) + (serasa.quantidade_cheques_sf || 0);
        const totalDividasVal = (serasa.valor_dividas_comerciais || 0) + (serasa.valor_dividas_bancarias || 0) + (serasa.valor_dividas_vencidas || 0) + (serasa.valor_total_protestos || 0) + (serasa.valor_total_cheques_sf || 0);
        const parecerCredito = analise?.parecer_credito || '';

        for (const [indiceLinha, linha] of (linhasPorNegociacao.get(neg.id) || []).entries()) {
            const { uc, numero } = linha;
            const dte = uc?.dados_tecnicos_equatorial;
            // Números pelos quais a UC aparece nas faturas e nas listas de rateio.
            const chaves = chavesDaLinha(linha);
            const history: any[] = dte?.historyTable || [];

            // Calculate delay average (same logic as UcAnalysisModal)
            const validHistory = history.filter((r: any) => !r.semRegistro);
            const delays = validHistory.map((r: any) => Number(r.delayDays) || 0);
            const avgDelay = validHistory.length > 0
                ? Math.round(delays.reduce((s, d) => s + d, 0) / validHistory.length)
                : 0;

            // Monthly consumption — cada slot ({ month: "mm/aa", consumo, semRegistro })
            // vai pra coluna do SEU mês, não pra posição em que está no array.
            const consumoPorMes = new Map<number, number>();
            const indicesDaLinha: number[] = [];
            const numericConsumo: number[] = [];
            for (let i = 0; i < NUM_HISTORY_SLOTS; i++) {
                const entry = history[i];
                if (!entry || entry.semRegistro) continue;
                const val = parseBrNumber(entry.consumo);
                if (!(val > 0)) continue;
                numericConsumo.push(val);
                const indice = mesParaIndice(entry.month);
                if (indice === null) continue;
                indicesDaLinha.push(indice);
                // Mês repetido no mesmo histórico: fica o primeiro, que é o mais recente.
                if (!consumoPorMes.has(indice)) consumoPorMes.set(indice, val);
            }
            // Sem consumo na análise técnica: meses do quadro da fatura mais recente.
            // Só preenche as colunas de mês — a média continua a da UC/negociação.
            const consumoDaFatura = numericConsumo.length === 0 ? historicoDaFatura(chaves) : [];
            for (const { indice, consumo } of consumoDaFatura) {
                indicesDaLinha.push(indice);
                consumoPorMes.set(indice, consumo);
            }
            const monthConsumo: (number | string)[] = mesesGrade.map(m => consumoPorMes.get(m) ?? '');

            // Período coberto pela própria linha — sem isso, uma UC cujo histórico
            // caia fora da janela apareceria só como linha vazia, sem explicação.
            // Tem UC com consumo gravado e nenhum mês (historyTable com month ''):
            // aí não dá pra colocar em coluna nenhuma, mas a média usa os valores,
            // então a linha precisa dizer por que as colunas estão vazias.
            const periodoHistorico = indicesDaLinha.length > 0
                ? `${indiceParaLabel(Math.min(...indicesDaLinha))} a ${indiceParaLabel(Math.max(...indicesDaLinha))}`
                    + (consumoDaFatura.length > 0 ? ' (faturas)' : '')
                : (numericConsumo.length > 0 ? 'sem mês no histórico' : '');

            // Consumo gravado na UC — o número oficial que o rateio usa e que, somado,
            // dá o kWh da negociação no Kanban (a análise o regrava quando diverge).
            // Antes saía a média simples dos meses do histórico, que não aparece em
            // lugar nenhum do sistema: 272 de 323 negociações não batiam com o CRM.
            const consumoUc = uc && uc.consumo_medio_kwh != null ? Number(uc.consumo_medio_kwh) || 0 : '';

            // UC status + checks técnicos da análise da UC
            const ucStatus = dte?.status || '';
            const checks = (dte?.checks || {}) as any;
            const obsTecnicas = [checks.explicacaoGD, checks.explicacaoConsumo500, checks.explicacaoGrupoB]
                .map((t: any) => (typeof t === 'string' ? t.trim() : ''))
                .filter(Boolean)
                .join(' | ');

            const statusRateio = uc ? STATUS_RATEIO_UC_META[getStatusRateioUc(uc)].label : '';

            // Build month header labels for this row (we'll use the first UC's labels for the shared header)
            rows.push([
                decisao,                                        // Status Análise
                avgDelay,                                       // Média de Atraso
                risco,                                          // Classificação
                dataAss,                                        // Data Ass
                dataAnalise,                                    // Data Análise
                soDigitos(uc?.numero_uc) || numero,             // UC
                uc?.titular_fatura || neg.cliente_nome || '',    // Nome/Razão Social
                uc?.cpf_cnpj_titular || neg.cpf_cnpj || '',     // CPF/CNPJ
                dataIsoParaBr(uc?.data_nascimento_titular || uc?.data_nascimento_cliente), // Nascimento
                // Dias do ciclo: análise técnica → histórico manual → colunas da UC → fatura mais recente
                primeiroDia(dte?.dataLeitura, uc?.hist_manual_leitura_atual, uc?.data_leitura,
                    dataMaisRecente(indiceFaturas, chaves, 'leitura_atual')),            // Dia Leitura
                primeiroDia(dte?.dataApresentacao, uc?.hist_manual_data_apresentacao, uc?.data_apresentacao,
                    dataMaisRecente(indiceFaturas, chaves, 'apresentacao')),             // Dia Apresentação
                primeiroDia(dte?.dataVencimento, uc?.hist_manual_data_vencimento, uc?.data_vencimento,
                    dataMaisRecente(indiceFaturas, chaves, 'vencimento')),               // Dia Vencimento
                vendedor,                                       // Vendedor
                analista,                                       // Analista
                percentualDeFracao(uc?.desconto_tarifa),        // Desconto HFP(%) — gravado como fração
                percentualDeFracao(uc?.desconto_bandeira),      // Desconto Bandeira
                uc?.tipo_ligacao || dte?.tipoFornecimento || '', // Instalação
                uc?.grupo_tarifario || dte?.grupo || '',        // Grupo
                uc?.whatsapp_cliente || neg.whatsapp || '',     // Contato
                uc?.email_cliente || neg.email || '',           // E-mail
                ucStatus,                                       // Status da UC
                simNao(checks.injetando),                       // Terceiros Injetando
                simNao(checks.distratoRecebido),                // Distrato Recebido
                simNao(checks.possuiGD),                        // Possui GD
                simNao(checks.mesmoTitular),                    // Titular Confere
                simNao(checks.atualizada),                      // Fatura Atualizada
                obsTecnicas,                                    // Obs. Técnicas
                scoreSerasa,                                    // Score Serasa
                chancePag,                                      // Chance Pag. / Prob. Inadimpl.
                riscoCredito,                                   // Risco de Crédito (Serasa)
                situacaoRF,                                     // Situação Receita Federal
                scoreMotor,                                     // Score Motor de Risco
                riscoFinal,                                     // Risco Final
                totalDividasQtd,                                // Qtd. Dívidas
                totalDividasVal,                                // Valor Total Dívidas
                parecerCredito,                                 // Parecer de Crédito
                ...monthConsumo,                                // colunas de mês (calendário)
                periodoHistorico,                               // Período do Histórico
                consumoUc,                                      // Consumo da UC (kWh)
                analise?.ressalvas_condicoes || '',             // Observação / Ressalvas
                statusRateio,                                   // Status de Rateio
                inclusaoNoRateio(indiceRateio, chaves, uc?.rateio_vinculado_em), // Inclusão no Rateio
                primeiraFatura(indiceFaturas, chaves),          // 1ª Fatura Vigor
                neg.id,                                         // Nº Negociação
                neg.cliente_nome || '',                         // Cliente no CRM
                situacaoCrm,                                    // Situação no CRM
                // Só na 1ª linha: somar a coluna dá o total do Kanban sem contar duas vezes
                // a negociação de várias UCs.
                indiceLinha === 0 ? kwhNegociacao : '',         // kWh da negociação (CRM)
            ]);
        }
    }

    const monthHeaders = mesesGrade.map(m => `${indiceParaLabel(m)} (kWh)`);

    // ── Define Headers ─────────────────────────────────────────────
    const CREDIT_COL_COUNT = 9; // colunas da Análise de Crédito (Serasa)
    // Checks técnicos da UC — pedido da Dhaiane/Meryene (29/07/2026) para acompanhar
    // a saída das usinas de terceiros conforme a Vigor assume a injeção. Ficam logo
    // depois de "Status UC" porque explicam o motivo daquele status.
    const UC_CHECK_COL_COUNT = 6;
    const FIXED_COL_COUNT = 21 + UC_CHECK_COL_COUNT + CREDIT_COL_COUNT; // columns before month data
    const headers = [
        'Status Análise', 'Média de Atraso', 'Classificação', 'Data Ass.', 'Data Análise',
        'UC', 'Nome/Razão Social', 'CPF/CNPJ', 'Nascimento',
        'Dia Leitura', 'Dia Apresentação', 'Dia Vencimento',
        'Vendedor', 'Analista', 'Desconto HFP(%)', 'Desconto Bandeira',
        'Instalação', 'Grupo', 'Contato', 'E-mail', 'Status UC',
        'Terceiros Injetando', 'Distrato Recebido', 'Possui GD', 'Titular Confere', 'Fatura Atualizada', 'Obs. Técnicas',
        'Score Serasa', 'Chance Pag./Inadimpl.', 'Risco Crédito', 'Situação RF',
        'Score Motor', 'Risco Final', 'Qtd. Dívidas', 'Valor Dívidas', 'Parecer Crédito',
        ...monthHeaders,
        'Período do Histórico', 'Consumo da UC (kWh)', 'Observação', 'Status de Rateio',
        // Pedido do Vinicius Alves (13/10/2026). Mês, não dia: a entrada no rateio
        // só é registrada pela lista mensal, e a fatura é do mês de referência.
        'Inclusão no Rateio (mês)', '1ª Fatura Vigor (mês)',
        // Conferência com o Kanban (20/10/2026): no fim, para não deslocar as colunas
        // que já são usadas em PROCV.
        'Nº Negociação', 'Cliente no CRM', 'Situação no CRM', 'kWh da negociação (CRM)',
    ];

    const UC_CHECK_START_COL = 21;                               // logo após "Status UC"
    const UC_CHECK_OBS_COL = UC_CHECK_START_COL + UC_CHECK_COL_COUNT - 1;
    const CREDIT_START_COL = FIXED_COL_COUNT - CREDIT_COL_COUNT;
    const CREDIT_END_COL = FIXED_COL_COUNT - 1;
    const MONTH_START_COL = FIXED_COL_COUNT;
    const MONTH_END_COL = MONTH_START_COL + mesesGrade.length - 1;
    const PERIODO_COL = MONTH_END_COL + 1;
    const MEDIA_COL = PERIODO_COL + 1;
    const OBS_COL = MEDIA_COL + 1;
    const INCLUSAO_RATEIO_COL = OBS_COL + 2;      // depois de "Status de Rateio"
    const PRIMEIRA_FATURA_COL = INCLUSAO_RATEIO_COL + 1;
    const NEG_ID_COL = PRIMEIRA_FATURA_COL + 1;
    const CLIENTE_CRM_COL = NEG_ID_COL + 1;
    const SITUACAO_CRM_COL = CLIENTE_CRM_COL + 1;
    const KWH_NEG_COL = SITUACAO_CRM_COL + 1;

    // ── Build Worksheet ────────────────────────────────────────────
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];

    // Row 0: Title
    const titleRow = Array(headers.length).fill('');
    titleRow[0] = `Relatório de Validação de Dados — ${new Date().toLocaleDateString('pt-BR')}`;
    wsData.push(titleRow);

    // Row 1: Subtitle
    const subtitleRow = Array(headers.length).fill('');
    subtitleRow[0] = `${rows.length} registro${rows.length !== 1 ? 's' : ''} exportado${rows.length !== 1 ? 's' : ''}`
        + ` · consumo de ${indiceParaLabel(mesesGrade[mesesGrade.length - 1])} a ${indiceParaLabel(mesesGrade[0])}`
        + (avisos.length > 0 ? ` · ⚠ não carregou: ${avisos.join(', ')} (colunas ligadas a isso podem sair vazias)` : '')
        + ` · "kWh da negociação (CRM)" é o do card no Kanban, só na 1ª linha de cada negociação`
        + ` · Vigor Energy`;
    wsData.push(subtitleRow);

    // Row 2: Empty spacer
    wsData.push(Array(headers.length).fill(''));

    // Row 3: Headers
    wsData.push(headers);

    // Rows 4+: Data
    wsData.push(...rows);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Apply Styles ───────────────────────────────────────────────
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

    // Title row (row 0)
    for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = titleStyle;
        else ws[ref] = { v: '', t: 's', s: titleStyle };
    }
    // Merge title
    ws['!merges'] = ws['!merges'] || [];
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } });

    // Subtitle row (row 1)
    const subtitleStyle: XLSX.CellStyle = {
        font: { ...baseFont, sz: 10, italic: true, color: { rgb: '6B7280' } },
        fill: { fgColor: { rgb: CLR.headerBg } },
        alignment: { horizontal: 'left', vertical: 'center' },
    };
    for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r: 1, c });
        if (ws[ref]) ws[ref].s = subtitleStyle;
        else ws[ref] = { v: '', t: 's', s: subtitleStyle };
    }
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } });

    // Spacer row (row 2)
    for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r: 2, c });
        if (ws[ref]) ws[ref].s = { fill: { fgColor: { rgb: 'FFFFFF' } } };
        else ws[ref] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } };
    }

    // Header row (row 3)
    const headerRow = 3;
    for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r: headerRow, c });
        if (ws[ref]) ws[ref].s = headerStyle;
    }

    // Data rows (row 4+)
    const dataStartRow = 4;
    for (let r = dataStartRow; r <= range.e.r; r++) {
        const isEven = (r - dataStartRow) % 2 === 0;
        for (let c = range.s.c; c <= range.e.c; c++) {
            const ref = XLSX.utils.encode_cell({ r, c });
            if (!ws[ref]) ws[ref] = { v: '', t: 's', s: dataStyle(isEven) };

            const val = ws[ref].v;

            // Column-specific styling
            if (c === 0) {
                // Status Análise
                ws[ref].s = getStatusStyle(String(val || ''), isEven);
            } else if (c === 1) {
                // Média de Atraso
                ws[ref].s = getDelayStyle(Number(val) || 0, isEven);
            } else if (c === 2) {
                // Classificação (Risco)
                ws[ref].s = getRiscoStyle(String(val || ''), isEven);
            } else if (c >= MONTH_START_COL && c <= MONTH_END_COL) {
                // Month consumption columns — numeric
                ws[ref].s = dataNumStyle(isEven);
            } else if (c === PERIODO_COL || c === INCLUSAO_RATEIO_COL || c === PRIMEIRA_FATURA_COL || c === NEG_ID_COL) {
                // Período do Histórico, Inclusão no Rateio, 1ª Fatura, Nº Negociação — centralizado
                ws[ref].s = dataCenterStyle(isEven);
            } else if (c === MEDIA_COL || c === KWH_NEG_COL) {
                // Consumo da UC e kWh da negociação — bold numeric
                ws[ref].s = {
                    ...dataNumStyle(isEven),
                    font: { ...baseFont, bold: true, color: { rgb: CLR.subHeaderBg } },
                };
            } else if (c === 3 || c === 4 || c === 8 || c === 9 || c === 10 || c === 11) {
                // Date columns — centered
                ws[ref].s = dataCenterStyle(isEven);
            } else if (c >= CREDIT_START_COL && c <= CREDIT_END_COL) {
                // Análise de Crédito: Score Serasa / Score Motor / Qtd. / Valor → numérico; demais → texto
                const isNum = c === CREDIT_START_COL       // Score Serasa
                    || c === CREDIT_START_COL + 4          // Score Motor
                    || c === CREDIT_START_COL + 6          // Qtd. Dívidas
                    || c === CREDIT_START_COL + 7;         // Valor Dívidas
                ws[ref].s = isNum ? dataNumStyle(isEven) : dataStyle(isEven);
            } else {
                ws[ref].s = dataStyle(isEven);
            }
        }
    }

    // ── Column Widths ──────────────────────────────────────────────
    const colWidths: { wpx: number }[] = headers.map((h, i) => {
        // Calculate max width from header + data
        let maxLen = h.length;
        for (const row of rows) {
            const val = row[i] != null ? String(row[i]) : '';
            if (val.length > maxLen) maxLen = val.length;
        }
        const baseW = Math.ceil(maxLen * 8) + 20;

        // Enforce min/max per column type
        if (i === 0) return { wpx: Math.max(200, baseW) };                     // Status
        if (i === 1) return { wpx: 110 };                                       // Média Atraso
        if (i === 2) return { wpx: 110 };                                       // Classificação
        if (i === 6) return { wpx: Math.max(200, Math.min(baseW, 300)) };       // Nome/Razão
        if (i === 7) return { wpx: 140 };                                       // CPF/CNPJ
        if (i === UC_CHECK_OBS_COL) return { wpx: Math.max(200, Math.min(baseW, 360)) }; // Obs. Técnicas
        if (i >= UC_CHECK_START_COL && i < UC_CHECK_OBS_COL) return { wpx: 105 };        // Sim/Não dos checks
        if (i === CREDIT_END_COL) return { wpx: Math.max(220, Math.min(baseW, 400)) }; // Parecer Crédito
        if (i >= MONTH_START_COL && i <= MONTH_END_COL) return { wpx: 90 };     // Month cols
        if (i === PERIODO_COL) return { wpx: 130 };                             // Período do Histórico
        if (i === MEDIA_COL) return { wpx: 120 };                               // Consumo da UC
        if (i === OBS_COL) return { wpx: Math.max(200, Math.min(baseW, 400)) }; // Observação
        if (i === CLIENTE_CRM_COL) return { wpx: Math.max(200, Math.min(baseW, 300)) };
        if (i === SITUACAO_CRM_COL) return { wpx: Math.max(240, Math.min(baseW, 340)) };
        if (i === KWH_NEG_COL) return { wpx: 150 };
        return { wpx: Math.min(Math.max(baseW, 90), 200) };
    });
    ws['!cols'] = colWidths;

    // Row heights
    ws['!rows'] = [
        { hpx: 42 },  // Title
        { hpx: 22 },  // Subtitle
        { hpx: 8 },   // Spacer
        { hpx: 36 },  // Headers
        ...rows.map(() => ({ hpx: 28 })),
    ];

    // ── Remove gridlines & print setup ─────────────────────────────
    ws['!sheetprops'] = { showGridLines: false } as any;

    // ── Finalize ───────────────────────────────────────────────────
    XLSX.utils.book_append_sheet(wb, ws, 'Validação de Dados');

    const now = new Date();
    const datePart = now.toLocaleDateString('pt-BR').replace(/\//g, '-');
    const timePart = now.toLocaleTimeString('pt-BR').replace(/:/g, '');
    const timestamp = `${datePart}_${timePart}`;
    XLSX.writeFile(wb, `Relatorio_Analise_Cadastral_${timestamp}.xlsx`);
}
